<script setup lang="ts">
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
</script>

<template>
  <!-- NDC Allocation Section -->
  <div id="ndc-allocation-section">
    <main id="ndc-allocation-main" class="flex-1 p-5 overflow-y-auto">
      <!-- Action Buttons -->
      <div id="action-buttons" class="flex justify-between items-center mb-6">
        <div class="flex space-x-3">
          <button
            class="px-4 py-2 bg-forest-green text-white rounded-md hover:bg-green-700 flex items-center"
          >
            <i class="fa-solid fa-save mr-2"></i>
            {{ $t('government.administration.ndcAllocation.buttons.saveChanges') }}
          </button>
          <button
            class="px-4 py-2 border border-light-gray text-gray-700 rounded-md hover:bg-gray-50 flex items-center"
          >
            <i class="fa-solid fa-undo mr-2"></i>
            {{ $t('government.administration.ndcAllocation.buttons.reset') }}
          </button>
        </div>
        <div class="text-sm text-gray-600">{{ $t('government.administration.ndcAllocation.lastUpdated') }} March 15, 2024</div>
      </div>

      <!-- NDC Allocation Section -->
      <div
        id="ndc-allocation-section"
        class="bg-white p-5 rounded-md border border-light-gray mb-6"
      >
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-medium text-dark-navy">
            {{ $t('government.administration.ndcAllocation.allocation.title') }}
          </h3>
          <div class="text-sm text-gray-600">
            {{ $t('government.administration.ndcAllocation.allocation.total') }} <span id="ndc-total" class="font-medium">30%</span>
          </div>
        </div>

        <!-- All Sectors Toggle -->
        <div id="ndc-all-toggle" class="mb-4 p-4 bg-gray-50 rounded-md">
          <label class="flex items-center">
            <input
              type="checkbox"
              class="mr-3 w-4 h-4 text-forest-green"
              id="ndc-all-checkbox"
            />
            <span class="font-medium">{{ $t('government.administration.ndcAllocation.allocation.applyToAll') }}</span>
            <input
              type="number"
              class="ml-4 px-3 py-2 border border-light-gray rounded-md w-20"
:placeholder="$t('government.administration.ndcAllocation.placeholders.percentage')"
              id="ndc-all-percent"
            />
          </label>
        </div>

        <!-- Sector-specific Allocations -->
        <div id="ndc-sectors" class="space-y-4">
          <div
            class="flex items-center justify-between p-4 border border-light-gray rounded-md"
          >
            <label class="flex items-center flex-1">
              <input
                type="checkbox"
                class="mr-3 w-4 h-4 text-forest-green"
                checked
              />
              <span class="font-medium">{{ $t('government.administration.ndcAllocation.sectors.renewableEnergy') }}</span>
            </label>
            <input
              type="number"
              class="px-3 py-2 border border-light-gray rounded-md w-20"
              value="15"
:placeholder="$t('government.administration.ndcAllocation.placeholders.percentage')"
            />
          </div>
          <div
            class="flex items-center justify-between p-4 border border-light-gray rounded-md"
          >
            <label class="flex items-center flex-1">
              <input
                type="checkbox"
                class="mr-3 w-4 h-4 text-forest-green"
                checked
              />
              <span class="font-medium">{{ $t('government.administration.ndcAllocation.sectors.forestryLandUse') }}</span>
            </label>
            <input
              type="number"
              class="px-3 py-2 border border-light-gray rounded-md w-20"
              value="10"
:placeholder="$t('government.administration.ndcAllocation.placeholders.percentage')"
            />
          </div>
          <div
            class="flex items-center justify-between p-4 border border-light-gray rounded-md"
          >
            <label class="flex items-center flex-1">
              <input
                type="checkbox"
                class="mr-3 w-4 h-4 text-forest-green"
                checked
              />
              <span class="font-medium">{{ $t('government.administration.ndcAllocation.sectors.agriculture') }}</span>
            </label>
            <input
              type="number"
              class="px-3 py-2 border border-light-gray rounded-md w-20"
              value="5"
:placeholder="$t('government.administration.ndcAllocation.placeholders.percentage')"
            />
          </div>
          <div
            class="flex items-center justify-between p-4 border border-light-gray rounded-md"
          >
            <label class="flex items-center flex-1">
              <input type="checkbox" class="mr-3 w-4 h-4 text-forest-green" />
              <span class="font-medium">{{ $t('government.administration.ndcAllocation.sectors.transportation') }}</span>
            </label>
            <input
              type="number"
              class="px-3 py-2 border border-light-gray rounded-md w-20"
              value="0"
:placeholder="$t('government.administration.ndcAllocation.placeholders.percentage')"
            />
          </div>
          <div
            class="flex items-center justify-between p-4 border border-light-gray rounded-md"
          >
            <label class="flex items-center flex-1">
              <input type="checkbox" class="mr-3 w-4 h-4 text-forest-green" />
              <span class="font-medium">{{ $t('government.administration.ndcAllocation.sectors.industrialProcesses') }}</span>
            </label>
            <input
              type="number"
              class="px-3 py-2 border border-light-gray rounded-md w-20"
              value="0"
:placeholder="$t('government.administration.ndcAllocation.placeholders.percentage')"
            />
          </div>
          <div
            class="flex items-center justify-between p-4 border border-light-gray rounded-md"
          >
            <label class="flex items-center flex-1">
              <input type="checkbox" class="mr-3 w-4 h-4 text-forest-green" />
              <span class="font-medium">{{ $t('government.administration.ndcAllocation.sectors.wasteManagement') }}</span>
            </label>
            <input
              type="number"
              class="px-3 py-2 border border-light-gray rounded-md w-20"
              value="0"
:placeholder="$t('government.administration.ndcAllocation.placeholders.percentage')"
            />
          </div>
        </div>
      </div>

      <!-- National Buffer Account Section -->
      <div
        id="buffer-account-section"
        class="bg-white p-5 rounded-md border border-light-gray mb-6"
      >
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-medium text-dark-navy">
            {{ $t('government.administration.ndcAllocation.bufferAccount.title') }}
          </h3>
          <div class="text-sm text-gray-600">
            {{ $t('government.administration.ndcAllocation.bufferAccount.total') }} <span id="buffer-total" class="font-medium">2%</span>
          </div>
        </div>

        <!-- All Sectors Toggle -->
        <div id="buffer-all-toggle" class="mb-4 p-4 bg-gray-50 rounded-md">
          <label class="flex items-center">
            <input
              type="checkbox"
              class="mr-3 w-4 h-4 text-forest-green"
              id="buffer-all-checkbox"
            />
            <span class="font-medium">{{ $t('government.administration.ndcAllocation.bufferAccount.applyToAll') }}</span>
            <input
              type="number"
              class="ml-4 px-3 py-2 border border-light-gray rounded-md w-20"
:placeholder="$t('government.administration.ndcAllocation.placeholders.percentage')"
              id="buffer-all-percent"
            />
          </label>
        </div>

        <!-- Sector-specific Buffer -->
        <div id="buffer-sectors" class="space-y-4">
          <div
            class="flex items-center justify-between p-4 border border-light-gray rounded-md"
          >
            <label class="flex items-center flex-1">
              <input
                type="checkbox"
                class="mr-3 w-4 h-4 text-forest-green"
                checked
              />
              <span class="font-medium">{{ $t('government.administration.ndcAllocation.sectors.renewableEnergy') }}</span>
            </label>
            <input
              type="number"
              class="px-3 py-2 border border-light-gray rounded-md w-20"
              value="2"
:placeholder="$t('government.administration.ndcAllocation.placeholders.percentage')"
            />
          </div>
          <div
            class="flex items-center justify-between p-4 border border-light-gray rounded-md"
          >
            <label class="flex items-center flex-1">
              <input type="checkbox" class="mr-3 w-4 h-4 text-forest-green" />
              <span class="font-medium">{{ $t('government.administration.ndcAllocation.sectors.forestryLandUse') }}</span>
            </label>
            <input
              type="number"
              class="px-3 py-2 border border-light-gray rounded-md w-20"
              value="0"
:placeholder="$t('government.administration.ndcAllocation.placeholders.percentage')"
            />
          </div>
          <div
            class="flex items-center justify-between p-4 border border-light-gray rounded-md"
          >
            <label class="flex items-center flex-1">
              <input type="checkbox" class="mr-3 w-4 h-4 text-forest-green" />
              <span class="font-medium">{{ $t('government.administration.ndcAllocation.sectors.agriculture') }}</span>
            </label>
            <input
              type="number"
              class="px-3 py-2 border border-light-gray rounded-md w-20"
              value="0"
:placeholder="$t('government.administration.ndcAllocation.placeholders.percentage')"
            />
          </div>
        </div>
      </div>

      <!-- National Transaction Account Section -->
      <div
        id="transaction-account-section"
        class="bg-white p-5 rounded-md border border-light-gray"
      >
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-medium text-dark-navy">
            {{ $t('government.administration.ndcAllocation.transactionAccount.title') }}
          </h3>
          <div class="text-sm text-gray-600">
            {{ $t('government.administration.ndcAllocation.transactionAccount.total') }} <span id="transaction-total" class="font-medium">30%</span>
          </div>
        </div>

        <!-- All Sectors Toggle -->
        <div id="transaction-all-toggle" class="mb-4 p-4 bg-gray-50 rounded-md">
          <label class="flex items-center">
            <input
              type="checkbox"
              class="mr-3 w-4 h-4 text-forest-green"
              checked
              id="transaction-all-checkbox"
            />
            <span class="font-medium">{{ $t('government.administration.ndcAllocation.transactionAccount.applyToAll') }}</span>
            <input
              type="number"
              class="ml-4 px-3 py-2 border border-light-gray rounded-md w-20"
              value="30"
:placeholder="$t('government.administration.ndcAllocation.placeholders.percentage')"
              id="transaction-all-percent"
            />
          </label>
        </div>

        <!-- Sector-specific Transaction -->
        <div id="transaction-sectors" class="space-y-4 opacity-50">
          <div
            class="flex items-center justify-between p-4 border border-light-gray rounded-md"
          >
            <label class="flex items-center flex-1">
              <input
                type="checkbox"
                class="mr-3 w-4 h-4 text-forest-green"
                disabled
              />
              <span class="font-medium">{{ $t('government.administration.ndcAllocation.sectors.renewableEnergy') }}</span>
            </label>
            <input
              type="number"
              class="px-3 py-2 border border-light-gray rounded-md w-20"
              value="30"
:placeholder="$t('government.administration.ndcAllocation.placeholders.percentage')"
              disabled
            />
          </div>
          <div
            class="flex items-center justify-between p-4 border border-light-gray rounded-md"
          >
            <label class="flex items-center flex-1">
              <input
                type="checkbox"
                class="mr-3 w-4 h-4 text-forest-green"
                disabled
              />
              <span class="font-medium">{{ $t('government.administration.ndcAllocation.sectors.forestryLandUse') }}</span>
            </label>
            <input
              type="number"
              class="px-3 py-2 border border-light-gray rounded-md w-20"
              value="30"
:placeholder="$t('government.administration.ndcAllocation.placeholders.percentage')"
              disabled
            />
          </div>
          <div
            class="flex items-center justify-between p-4 border border-light-gray rounded-md"
          >
            <label class="flex items-center flex-1">
              <input
                type="checkbox"
                class="mr-3 w-4 h-4 text-forest-green"
                disabled
              />
              <span class="font-medium">{{ $t('government.administration.ndcAllocation.sectors.agriculture') }}</span>
            </label>
            <input
              type="number"
              class="px-3 py-2 border border-light-gray rounded-md w-20"
              value="30"
:placeholder="$t('government.administration.ndcAllocation.placeholders.percentage')"
              disabled
            />
          </div>
        </div>
      </div>
    </main>
  </div>
</template>
